<div class="modal-header">
  <h5 class="modal-title">{{ (isExisting ? 'PUNCHOUT_EDIT_CONFIG' : 'PUNCHOUT_NEW_CONFIG') | translate }}</h5>
  <button type="button" class="btn-close" (click)="cancel()" [attr.aria-label]="'CLOSE' | translate"></button>
</div>

<div class="modal-body">
  <form *ngIf="form" [formGroup]="form">
    <div class="form-group mb-3">
      <label for="punchoutConfigName" translate>NAME</label>
      <input
        id="punchoutConfigName"
        type="text"
        class="form-control"
        formControlName="name"
        [class.is-invalid]="form.controls['name'].invalid && form.controls['name'].touched"
      />
      <div class="invalid-feedback" translate>FIELD_REQUIRED</div>
    </div>

    <div class="form-group mb-3">
      <label for="punchoutConfigFormat" translate>FORMAT</label>
      <select id="punchoutConfigFormat" class="form-control" formControlName="format">
        <option value="oci">OCI</option>
        <option value="cxml">cXML</option>
      </select>
    </div>

    <div class="form-group mb-3">
      <label for="punchoutConfigQualifier" translate>QUALIFIER</label>
      <select
        id="punchoutConfigQualifier"
        class="form-control"
        formControlName="qualifier"
        [class.is-invalid]="form.controls['qualifier'].invalid && form.controls['qualifier'].touched"
      >
        <option *ngFor="let option of mapperOptions" [value]="option.identifier">
          {{ option.label }} ({{ option.identifier }})
        </option>
      </select>
      <div class="invalid-feedback" translate>FIELD_REQUIRED</div>
    </div>

    <!-- Read-only: the backend endpoint the buyer's system posts the punchout login to. -->
    <div class="form-group mb-3">
      <label for="punchoutConfigLoginUrl">{{ 'PUNCHOUT_LOGIN_URL' | translate }}</label>
      <div class="input-group">
        <input id="punchoutConfigLoginUrl" type="text" class="form-control" [value]="loginUrl" readonly />
        <button
          type="button"
          class="btn btn-outline-secondary"
          [title]="'COPY' | translate"
          (click)="copyUrl(loginUrl)"
        >
          <i class="fa-solid fa-copy"></i>
        </button>
      </div>
      <div class="form-text">{{ 'PUNCHOUT_LOGIN_URL_HELP' | translate }}</div>
    </div>

    <!--
      The users wired to this config, sitting under the login URL because the per-user example URLs
      are that same URL at one more level of specificity. Omitted entirely when the configs tab could
      not read the user list.
    -->
    @if (showConnectedUsers) {
      <div class="form-group mb-3">
        <!-- One toggle for the whole section: the per-user login URLs are the point of opening it. -->
        <button
          type="button"
          class="btn btn-link p-0 text-decoration-none"
          (click)="toggleConnectedUsers()"
          [attr.aria-expanded]="connectedUsersExpanded"
        >
          <i
            class="fa-solid me-1"
            [class.fa-chevron-right]="!connectedUsersExpanded"
            [class.fa-chevron-down]="connectedUsersExpanded"
          ></i>
          <span>{{ 'PUNCHOUT_CONNECTED_USERS' | translate }} ({{ connectedUserList.length }})</span>
        </button>

        @if (connectedUsersExpanded) {
          <!-- cXML has no credential-bearing login URL, so the buyer needs the endpoint above plus these users' credentials. -->
          @if (!supportsExampleLoginUrl) {
            <div class="form-text">{{ 'PUNCHOUT_CXML_LOGIN_IS_POSTED' | translate }}</div>
          }

          @if (connectedUserList.length === 0) {
            <div class="form-text">{{ 'PUNCHOUT_NO_CONNECTED_USERS' | translate }}</div>
          } @else {
            <!--
              Said once above the table rather than under every URL: repeating the label and help text
              per user left three lines of identical grey between one user and the next.
            -->
            @if (supportsExampleLoginUrl) {
              <div class="small text-muted mt-2">
                <span class="fw-semibold">{{ 'PUNCHOUT_EXAMPLE_LOGIN_URL' | translate }}</span>
                &mdash; {{ 'PUNCHOUT_EXAMPLE_LOGIN_URL_HELP' | translate }}
              </div>
            }

            <div class="table-responsive mt-2">
              <table class="table table-sm align-middle mb-0">
                <thead>
                  <tr class="small text-uppercase text-muted">
                    <th class="fw-semibold">{{ 'USERNAME' | translate }}</th>
                    <th class="fw-semibold">{{ 'CUSTOMER' | translate }}</th>
                    <th class="fw-semibold">{{ 'ASSORTMENT' | translate }}</th>
                    <th class="fw-semibold text-nowrap">{{ 'LAST_LOGIN' | translate }}</th>
                  </tr>
                </thead>
                <!--
                  One tbody per user so table-group-divider draws a heavy rule between users. A single
                  tbody cannot group a user with their URL row, which is what made the flat version
                  unreadable: the URL appeared to belong to the user below it.
                -->
                @for (user of connectedUserList; track user.username) {
                  <tbody class="table-group-divider">
                    <tr>
                      <td class="fw-semibold border-bottom-0">
                        <!-- New tab: a routerLink here would navigate behind the modal and discard unsaved edits. -->
                        <a
                          [routerLink]="['/admin/users-details', user.username]"
                          target="_blank"
                          rel="noopener"
                          class="font-monospace"
                          >{{ user.username }}</a
                        >
                        @if (!user.active) {
                          <span class="badge bg-secondary ms-2">{{ 'INACTIVE' | translate }}</span>
                        }
                      </td>
                      <td class="small border-bottom-0">{{ user.managedCompanyIds?.join(', ') || '-' }}</td>
                      <td class="small border-bottom-0">{{ user.assortment || '-' }}</td>
                      <td class="small text-nowrap border-bottom-0">
                        {{ latestLogin(user) ? (latestLogin(user) | date: 'yyyy-MM-dd HH:mm') : '-' }}
                      </td>
                    </tr>

                    <!-- Full-width and indented: the URL is ~100 chars, and the indent marks it as belonging to the user above. -->
                    @if (supportsExampleLoginUrl) {
                      <tr>
                        <td colspan="4" class="border-bottom-0 pt-0 ps-4">
                          <div class="input-group input-group-sm">
                            <input
                              type="text"
                              class="form-control form-control-sm font-monospace"
                              [value]="exampleLoginUrl(user)"
                              [attr.aria-label]="'PUNCHOUT_EXAMPLE_LOGIN_URL' | translate"
                              readonly
                            />
                            <button
                              type="button"
                              class="btn btn-outline-secondary"
                              [title]="'COPY' | translate"
                              (click)="copyUrl(exampleLoginUrl(user))"
                            >
                              <i class="fa-solid fa-copy"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    }
                  </tbody>
                }
              </table>
            </div>
          }
        }
      </div>
    }

    <div class="form-group mb-3">
      <label for="punchoutConfigRedirectUrl" translate>REDIRECT_URL</label>
      <div class="input-group">
        <input
          id="punchoutConfigRedirectUrl"
          type="text"
          class="form-control"
          formControlName="redirectUrl"
          [class.is-invalid]="form.controls['redirectUrl'].invalid && form.controls['redirectUrl'].touched"
        />
        <button
          type="button"
          class="btn btn-outline-secondary"
          [title]="suggestedRedirectUrl"
          (click)="useSuggestedRedirectUrl()"
        >
          <i class="fa-solid fa-wand-magic-sparkles me-1"></i>
          <span>{{ 'PUNCHOUT_SUGGEST_DEFAULT' | translate }}</span>
        </button>
        <div class="invalid-feedback" translate>FIELD_REQUIRED</div>
      </div>
      <div class="form-text">{{ 'PUNCHOUT_REDIRECT_URL_HELP' | translate }}</div>
    </div>

    <!-- Order intake: independent of catalog format (an OCI buyer can still send cXML orders). -->
    <hr />
    <h6 translate>PUNCHOUT_ORDER_INTAKE</h6>

    <div class="form-check form-switch mb-3">
      <input
        id="punchoutConfigOrderIntakeEnabled"
        type="checkbox"
        class="form-check-input"
        role="switch"
        formControlName="orderIntakeEnabled"
      />
      <label class="form-check-label" for="punchoutConfigOrderIntakeEnabled" translate
        >PUNCHOUT_ORDER_INTAKE_ENABLE</label
      >
    </div>

    <ng-container *ngIf="form.controls['orderIntakeEnabled'].value">
      <div class="form-group mb-3">
        <label for="punchoutConfigOrderIntakeHandler" translate>PUNCHOUT_ORDER_INTAKE_HANDLER</label>
        <select id="punchoutConfigOrderIntakeHandler" class="form-control" formControlName="orderIntakeHandlerId">
          <option *ngFor="let option of intakeHandlerOptions" [value]="option.id">
            {{ option.label }} ({{ option.id }})
          </option>
        </select>
      </div>

      <!-- The customer is resolved by the EDI flow from the GLN values in the incoming order,
           rather than pinned to one customer on the configuration. -->
      <div class="form-check form-switch mb-3">
        <input
          id="punchoutConfigOrderGlnMappingEnabled"
          type="checkbox"
          class="form-check-input"
          role="switch"
          formControlName="orderGlnMappingEnabled"
        />
        <label class="form-check-label" for="punchoutConfigOrderGlnMappingEnabled">{{
          'PUNCHOUT_ORDER_GLN_MAPPING_ENABLE' | translate
        }}</label>
        <div class="form-text">{{ 'PUNCHOUT_ORDER_GLN_MAPPING_HELP' | translate }}</div>
      </div>

      <div class="form-group mb-3">
        <label for="punchoutConfigOrderAuthMode" translate>PUNCHOUT_ORDER_AUTH_MODE</label>
        <select id="punchoutConfigOrderAuthMode" class="form-control" formControlName="orderAuthMode">
          <option value="NONE" translate>PUNCHOUT_ORDER_AUTH_MODE_NONE</option>
          <option value="SHARED_SECRET" translate>PUNCHOUT_ORDER_AUTH_MODE_SHARED_SECRET</option>
          <option value="BASIC_AUTH" translate>PUNCHOUT_ORDER_AUTH_MODE_BASIC_AUTH</option>
        </select>
      </div>

      <div class="form-group mb-3" *ngIf="orderAuthMode === 'SHARED_SECRET'">
        <label for="punchoutConfigOrderSharedSecret" translate>PUNCHOUT_ORDER_SHARED_SECRET</label>
        <input
          id="punchoutConfigOrderSharedSecret"
          type="password"
          autocomplete="new-password"
          class="form-control"
          formControlName="orderSharedSecret"
        />
      </div>

      <ng-container *ngIf="orderAuthMode === 'BASIC_AUTH'">
        <div class="form-group mb-3">
          <label for="punchoutConfigOrderBasicAuthUsername" translate>PUNCHOUT_ORDER_BASIC_AUTH_USERNAME</label>
          <input
            id="punchoutConfigOrderBasicAuthUsername"
            type="text"
            class="form-control"
            formControlName="orderBasicAuthUsername"
          />
        </div>
        <div class="form-group mb-3">
          <label for="punchoutConfigOrderBasicAuthPassword" translate>PUNCHOUT_ORDER_BASIC_AUTH_PASSWORD</label>
          <input
            id="punchoutConfigOrderBasicAuthPassword"
            type="password"
            autocomplete="new-password"
            class="form-control"
            formControlName="orderBasicAuthPassword"
          />
        </div>
      </ng-container>

      <div class="form-group mb-3" *ngIf="intakeUrl">
        <label for="punchoutConfigIntakeUrl" translate>PUNCHOUT_ORDER_INTAKE_URL</label>
        <div class="input-group">
          <input id="punchoutConfigIntakeUrl" type="text" class="form-control" [value]="intakeUrl" readonly />
          <button
            type="button"
            class="btn btn-outline-secondary"
            [title]="'COPY' | translate"
            (click)="copyUrl(intakeUrl)"
          >
            <i class="fa-solid fa-copy"></i>
          </button>
        </div>
      </div>
    </ng-container>
  </form>
</div>

<div class="modal-footer">
  <button
    *ngIf="isExisting"
    type="button"
    class="btn btn-outline-danger me-auto"
    [disabled]="deleting"
    (click)="delete($event)"
  >
    <i class="fa-solid fa-trash me-1"></i>
    <span translate>DELETE</span>
  </button>
  <button type="button" class="btn btn-secondary" (click)="cancel()">
    <span translate>CANCEL</span>
  </button>
  <button type="button" class="btn btn-primary" [disabled]="saving" (click)="save($event)">
    <span translate>SAVE</span>
  </button>
</div>
